import { Tracking } from 'src/apis/tracking';
import { setCookieWithExpiry } from 'src/utils/cookies';
import { getDeviceInfo } from 'src/utils/device';
import {
  getPurchasedFromCookie,
  updatePurchasedToCookie,
} from 'src/utils/products';

import { Api } from '../../apis/api';
import { Gtm } from '../../apis/gtm';
import { IImportLeadPayload } from '../../types/api';
import { IFunnelProps } from '../../types/funnel';
import { IProduct } from '../../types/product';
import {
  getVisibleElementById,
  getVisibleElementsById,
  setInputValue,
} from '../../utils/dom';
import { getLead, stateIsRequired } from '../../utils/lead';
import {
  getPersistedUrlParameters,
  isSuccessPage,
  isUpsellPage,
} from '../../utils/navigation';
import { getAppUtms, getCustomCookieParams } from '../../utils/utms';
import { validateLead, validateZipCode } from '../../utils/validations';
import { UiController } from '../ui/UiController';

export class LeadController {
  private config: IFunnelProps;
  lead: IImportLeadPayload;
  purchased: IProduct[] = [];

  static init = async (config: IFunnelProps) => {
    const { campaignId } = config;
    const { lead, purchased } = await getLead(campaignId);
    return new LeadController(config, lead, purchased);
  };

  private constructor(
    config: IFunnelProps,
    lead: IImportLeadPayload,
    purchased: IProduct[],
  ) {
    this.lead = lead;
    this.config = config;
    this.purchased = purchased;
    this.appendUrlParams();
    this.reportPurchase();
    if (this.lead?.orderId) {
      setCookieWithExpiry('order_id', this.lead.orderId, 10);
    }

    if (window.DEBUG) {
      console.log('LeadController:', this);
    }
  }

  private appendUrlParams = () => {
    const params = getPersistedUrlParameters(this.lead);
    if (params) {
      UiController.appendOnsiteLinksWithParams(params);
    }
  };

  private reportPurchase = () => {
    const orderId = this.lead?.orderId;
    const shouldReport = isUpsellPage() || isSuccessPage();
    const isFirstUpsell =
      parseInt(`${this.config.pageType.match(/\d+/g)}`, 10) === 1;
    const purchased = getPurchasedFromCookie();
    const lastProduct = this.getLastPurchased();

    if (isFirstUpsell) {
      const products = this.getPurchased();
      if (Array.isArray(products)) {
        products.forEach(el => {
          el.isDownsell = purchased?.[el.id]?.isDownsell;
          el.upsellKey =
            typeof el.upsellKey === 'number'
              ? el.upsellKey
              : purchased?.[el.id]?.upsellKey;
        });
        Gtm.trackTransaction(this.lead, lastProduct, undefined, products);
        Tracking.trackTransaction(this.lead, lastProduct, undefined, products);
        updatePurchasedToCookie(lastProduct, true);
      }
    } else {
      if (shouldReport && orderId && lastProduct) {
        const idToReport = lastProduct.id;
        lastProduct.isDownsell = purchased?.[lastProduct.id]?.isDownsell;
        lastProduct.upsellKey =
          typeof lastProduct.upsellKey === 'number'
            ? lastProduct.upsellKey
            : purchased?.[lastProduct.id]?.upsellKey;

        if (
          !purchased ||
          (!purchased?.[idToReport]?.isPurchased &&
            this.lead?.orderId == purchased?.[idToReport]?.orderId) ||
          purchased?.[idToReport]?.orderId !== this.lead?.orderId
        ) {
          Gtm.trackTransaction(this.lead, lastProduct, lastProduct.upsellKey);
          Tracking.trackTransaction(
            this.lead,
            lastProduct,
            lastProduct.upsellKey,
          );
          updatePurchasedToCookie(lastProduct, true);
        }
      }
    }
  };

  private getCheckoutLead = () => {
    const phoneInput = getVisibleElementById<'input'>('phoneNumber');
    const optinCheckboxes = getVisibleElementsById<'input'>('optinCheckbox');
    const optinCheckbox =
      optinCheckboxes?.filter(el => el?.checked)[0] || optinCheckboxes[0];
    const vipCheckbox = getVisibleElementById<'input'>('vipCheckbox');
    const phoneNumerIntl = UiController.getPhoneNumber('phoneNumber');

    const phoneNumber =
      this.config.klarnaEnabled || this.config.stripeKlarnaEnabled
        ? phoneInput?.value?.replace(/\D/g, '')
        : phoneNumerIntl;

    const state = stateIsRequired()
      ? getVisibleElementById<'input'>('state')?.value || undefined
      : '';
    const urlQueryParameters = getCustomCookieParams();

    // If subscribtion product selected
    const campaignId = window.subSelected
      ? this.config.subscriptionCampaignId
      : this.config.campaignId;

    return {
      client: window.BRAND,
      orderId: this.lead?.orderId || undefined,
      sessionId: this.config?.sessionId || this.lead?.sessionId,
      campaignId,
      paySource: this.lead?.paySource || undefined,

      customerId: this.lead?.customerId || undefined,
      firstName:
        getVisibleElementById<'input'>('firstName')?.value || undefined,
      lastName: getVisibleElementById<'input'>('lastName')?.value || undefined,
      phoneNumber,
      emailAddress:
        getVisibleElementById<'input'>('emailAddress')?.value || undefined,
      ipAddress: window.GEO.ip || '::1',

      state,
      city: getVisibleElementById<'input'>('city')?.value || undefined,
      country: getVisibleElementById<'input'>('country')?.value || undefined,
      address1: getVisibleElementById<'input'>('address1')?.value || undefined,
      postalCode:
        getVisibleElementById<'input'>('postalCode')?.value || undefined,
      shipPostalCode: this.lead?.shipPostalCode || undefined,
      billShipSame: true,
      custom_order_language: this.config.locale,
      custom_order_marketing_opt_in: optinCheckbox
        ? optinCheckbox.checked
        : undefined,
      custom_order_10_off_vip: vipCheckbox ? vipCheckbox.checked : undefined,
      custom_user_agent: navigator.userAgent,
      custom_http_referer: window.location.href,
      ...getDeviceInfo(),
      ...urlQueryParameters,
      ...getAppUtms(),
    } as IImportLeadPayload;
  };

  prefillForm = async () => {
    const lead = this.lead;
    setInputValue('emailAddress', lead?.emailAddress);
    setInputValue('firstName', lead?.firstName);
    setInputValue('lastName', lead?.lastName);
    setInputValue('city', lead?.city);
    setInputValue('address1', lead?.address1);
    setInputValue('postalCode', lead?.postalCode);
    setInputValue('country', lead?.country);
    setInputValue('state', lead?.state);
    if (this.config.klarnaEnabled || this.config.stripeKlarnaEnabled) {
      setInputValue('phoneNumber', lead?.phoneNumber || '');
    } else {
      waitFor('intlTelInputUtils', () => {
        const phoneInput = getVisibleElementById<'input'>('phoneNumber');
        if (phoneInput) {
          window.intlTelInputGlobals
            .getInstance(phoneInput as HTMLInputElement)
            .setNumber(lead?.phoneNumber || '');
        }
      });
    }
  };

  createLead = async (
    paymentType?: string,
    validate = false,
  ): Promise<IImportLeadPayload | null> => {
    this.lead = this.getCheckoutLead();
    try {
      if (validate) {
        validateLead(this.lead);
      }
      const shipPostalCode = await validateZipCode(
        this.lead.postalCode,
        this.lead.state,
        this.lead.country,
      );
      const payload = {
        ...this.lead,
        orderId: undefined,
        customerId: undefined,
        paySource: undefined,
        shipPostalCode: undefined,
        postalCode: shipPostalCode,
        firstName: this.lead.firstName?.replace(/\d/g, ''),
        lastName: this.lead.lastName?.replace(/\d/g, ''),
        custom3: `${window.location.origin + window.location.pathname}`,
      };
      if (paymentType == 'klarna') {
        payload.custom2 = paymentType;
      }

      const res = await Api.importLead(payload);
      this.lead = {
        ...this.lead,
        orderId: res.orderId,
        customerId: res.customerId,
        shipPostalCode,
      };

      Errors.setUserContext(this.lead);
      setCookieWithExpiry('order_id', res.orderId, 5);
      return this.lead;
    } catch (e) {
      Errors.report(e, {
        lead: this.lead,
        config: this.config,
      });
      return null;
    }
  };

  getLastPurchased = () => {
    const purchasedExcludingAddons = this.purchased.filter(
      el => el?.finalPrice > 0,
    );
    const lastIdx = purchasedExcludingAddons.length - 1;
    return purchasedExcludingAddons[lastIdx];
  };

  getPurchased = () => {
    return this.purchased;
  };

  setLeadInfo = (
    firstName?: string,
    lastName?: string,
    postalCode?: string,
    city?: string,
    country?: string,
    emailAddress?: string,
    phoneNumber?: string,
    address1?: string,
  ) => {
    if (firstName) {
      this.lead.firstName = firstName;
    }
    if (lastName) {
      this.lead.lastName = lastName;
    }
    if (postalCode) {
      this.lead.postalCode = postalCode;
    }
    if (city) {
      this.lead.city = city;
    }
    if (country) {
      this.lead.country = country;
    }
    if (emailAddress) {
      this.lead.emailAddress = emailAddress;
    }
    if (phoneNumber) {
      this.lead.phoneNumber = phoneNumber;
    }
    if (address1) {
      this.lead.address1 = address1;
    }
    if (window.DEBUG) {
      console.log('lead: ', this.lead);
    }
    return this.lead;
  };
}
